<template>
    <!-- Mobile Sidebar -->
    <TransitionRoot :show="sidebarOpen" as="template">
        <Dialog class="relative z-50 lg:hidden" @close="sidebarClosed">
            <TransitionChild
                as="template"
                enter="transition-opacity ease-linear duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/80" />
            </TransitionChild>

            <div class="fixed inset-0 flex">
                <TransitionChild
                    as="template"
                    enter="transition ease-in-out duration-300 transform"
                    enter-from="-translate-x-full"
                    enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leave-from="translate-x-0"
                    leave-to="-translate-x-full"
                >
                    <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
                        <!-- Close Button -->
                        <TransitionChild
                            as="template"
                            enter="ease-in-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in-out duration-300"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <div class="absolute top-0 left-full flex w-16 justify-center pt-5">
                                <button class="-m-2.5 p-2.5" type="button" @click="sidebarClosed">
                                    <span class="sr-only">Close sidebar</span>
                                    <XMarkIcon aria-hidden="true" class="h-6 w-6 text-white" />
                                </button>
                            </div>
                        </TransitionChild>

                        <!-- Sidebar Content (mobile drawer always shows labels) -->
                        <div class="sidebar-panel flex min-h-0 grow flex-col gap-y-3 overflow-hidden bg-white pb-4">
                            <div v-if="configStore.loaded" class="flex h-16 shrink-0 items-center px-6">
                                <img class="h-8 w-8 rounded-full object-cover" :src="companyLogo" />
                                <span class="ms-3 text-xl font-bold text-gray-900">{{ companyName }}</span>
                            </div>

                            <!-- Only the links scroll; the brand header above stays pinned. -->
                            <nav class="sidebar-scroll flex min-h-0 flex-1 flex-col overflow-y-auto px-6">
                                <ul class="flex flex-1 flex-col gap-y-4" role="list">
                                    <li>
                                        <ul class="-mx-2 space-y-1" role="list">
                                            <li v-for="entry in visibleNavigation" :key="navigationEntryId(entry)">
                                                <!-- Collapsible group -->
                                                <template v-if="entry.children">
                                                    <button
                                                        type="button"
                                                        :class="groupClass(entry)"
                                                        :aria-expanded="isGroupOpen(entry)"
                                                        @click="toggleGroup(entry)"
                                                    >
                                                        <component :is="entry.icon" :class="groupIconClass(entry)" aria-hidden="true" />
                                                        <span class="truncate">{{ entry.title }}</span>
                                                        <ChevronRightIcon
                                                            :class="[
                                                                'ms-auto h-4 w-4 shrink-0 text-gray-400 transition-transform duration-150',
                                                                isGroupOpen(entry) ? 'rotate-90' : '',
                                                            ]"
                                                            aria-hidden="true"
                                                        />
                                                    </button>

                                                    <ul
                                                        v-show="isGroupOpen(entry)"
                                                        class="ms-3 mt-1 space-y-1 border-s border-gray-200 ps-3"
                                                        role="list"
                                                    >
                                                        <li v-for="child in entry.children" :key="child.name">
                                                            <component
                                                                :is="child.href ? 'a' : RouterLink"
                                                                v-bind="linkTarget(child)"
                                                                :class="linkClass(child, false)"
                                                                :data-tour="sidebarTourAttr(child)"
                                                                @click="handleSidebarItemClick(child.name)"
                                                            >
                                                                <component :is="child.icon" :class="iconClass(child)" aria-hidden="true" />
                                                                <span class="truncate">{{ child.title }}</span>
                                                            </component>
                                                        </li>
                                                    </ul>
                                                </template>

                                                <!-- Top-level link -->
                                                <component
                                                    v-else
                                                    :is="entry.href ? 'a' : RouterLink"
                                                    v-bind="linkTarget(entry)"
                                                    :class="linkClass(entry, false)"
                                                    :data-tour="sidebarTourAttr(entry)"
                                                    @click="handleSidebarItemClick(entry.name)"
                                                >
                                                    <component :is="entry.icon" :class="iconClass(entry)" aria-hidden="true" />
                                                    <span class="truncate">{{ entry.title }}</span>
                                                </component>
                                            </li>
                                        </ul>
                                    </li>

                                    <li v-if="visibleManagerNavigation.length">
                                        <div class="text-xs/6 font-semibold text-gray-400">Admin Navigation</div>
                                        <ul class="-mx-2 mt-2 space-y-1" role="list">
                                            <template v-for="item in visibleManagerNavigation" :key="item.title">
                                                <li>
                                                    <!-- External link -->
                                                    <a
                                                        v-if="item.href"
                                                        :href="item.href"
                                                        :class="linkClass(item, false)"
                                                        @click="handleSidebarItemClick(item.name)"
                                                    >
                                                        <component :is="item.icon" :class="iconClass(item)" aria-hidden="true" />
                                                        <span class="truncate">{{ item.title }}</span>
                                                    </a>

                                                    <!-- Router link -->
                                                    <RouterLink
                                                        v-else-if="item.name"
                                                        :to="{ name: item.name }"
                                                        :class="linkClass(item, false)"
                                                        :data-tour="sidebarTourAttr(item)"
                                                        @click="handleSidebarItemClick(item.name)"
                                                    >
                                                        <component :is="item.icon" :class="iconClass(item)" aria-hidden="true" />
                                                        <span class="truncate">{{ item.title }}</span>
                                                    </RouterLink>
                                                </li>
                                            </template>
                                        </ul>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>

    <div
        v-if="true"
        data-tour="sidebar"
        :class="[
            'hidden transition-all duration-150 ease-in-out lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col',
            staticSidebarOpen ? 'lg:w-60' : 'lg:w-20',
        ]"
    >
        <!-- Padding sits on the header and the nav, not here, so the nav's scrollbar lands on the rail's edge. -->
        <div class="sidebar-panel flex min-h-0 grow flex-col gap-y-3 overflow-hidden border-r border-gray-200 bg-gray-50 pb-4">
            <div :class="['flex h-16 shrink-0 items-center', staticSidebarOpen ? 'px-6' : 'justify-center px-2']">
                <img :src="companyLogo" class="h-8 w-8 rounded-full object-cover" />
                <span v-if="staticSidebarOpen" class="ms-3 text-xl font-bold text-gray-900">{{ companyName }}</span>
            </div>

            <!-- Only the links scroll; the brand header above stays pinned. -->
            <nav :class="['sidebar-scroll flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto', staticSidebarOpen ? 'px-6' : 'px-2']">
                <ul class="flex flex-1 flex-col gap-y-4" role="list">
                    <li>
                        <ul class="-mx-2 space-y-1" role="list">
                            <li v-for="entry in visibleNavigation" :key="navigationEntryId(entry)">
                                <!-- Collapsible group -->
                                <template v-if="entry.children">
                                    <button
                                        type="button"
                                        :class="groupClass(entry, !staticSidebarOpen)"
                                        :aria-expanded="isGroupOpen(entry)"
                                        @click="toggleGroup(entry)"
                                    >
                                        <component :is="entry.icon" :class="groupIconClass(entry)" aria-hidden="true" />
                                        <template v-if="staticSidebarOpen">
                                            <span class="truncate">{{ entry.title }}</span>
                                        </template>
                                        <!-- The chevron is what marks a row as a group. On the rail it is
                                             the only thing distinguishing one, so it shows there too. -->
                                        <ChevronRightIcon
                                            :class="[
                                                'shrink-0 text-gray-400 transition-transform duration-150',
                                                staticSidebarOpen ? 'ms-auto h-4 w-4' : 'h-3 w-3',
                                                isGroupOpen(entry) ? 'rotate-90' : '',
                                            ]"
                                            aria-hidden="true"
                                        />
                                        <Tooltip v-if="!staticSidebarOpen" :text="entry.title" direction="right" />
                                    </button>

                                    <!-- Expanded: children sit behind an indent rule. On the rail there is
                                         no room to indent, so a tinted block ties them to their group. -->
                                    <ul
                                        v-show="isGroupOpen(entry)"
                                        :class="[
                                            'mt-1 space-y-1 rounded-md border border-gray-200 bg-gray-100/80',
                                            staticSidebarOpen ? 'ms-3 ps-3' : 'py-1',
                                        ]"
                                        role="list"
                                    >
                                        <li v-for="child in entry.children" :key="child.name">
                                            <component
                                                :is="child.href ? 'a' : RouterLink"
                                                v-bind="linkTarget(child)"
                                                :class="linkClass(child, !staticSidebarOpen)"
                                                :data-tour="sidebarTourAttr(child)"
                                                @click="handleSidebarItemClick(child.name)"
                                            >
                                                <component :is="child.icon" :class="iconClass(child, !staticSidebarOpen)" aria-hidden="true" />
                                                <span v-if="staticSidebarOpen" class="truncate">{{ child.title }}</span>
                                                <Tooltip v-if="!staticSidebarOpen" :text="child.title" direction="right" />
                                            </component>
                                        </li>
                                    </ul>
                                </template>

                                <!-- Top-level link -->
                                <component
                                    v-else
                                    :is="entry.href ? 'a' : RouterLink"
                                    v-bind="linkTarget(entry)"
                                    :class="linkClass(entry, !staticSidebarOpen)"
                                    :data-tour="sidebarTourAttr(entry)"
                                    @click="handleSidebarItemClick(entry.name)"
                                >
                                    <component :is="entry.icon" :class="iconClass(entry)" aria-hidden="true" />
                                    <span v-if="staticSidebarOpen" class="truncate">{{ entry.title }}</span>
                                    <Tooltip v-if="!staticSidebarOpen" :text="entry.title" direction="right" />
                                </component>
                            </li>
                        </ul>
                    </li>

                    <li v-if="visibleManagerNavigation.length">
                        <div v-if="staticSidebarOpen" class="text-xs/6 font-semibold text-gray-400">Admin Navigation</div>
                        <ul class="-mx-2 mt-2 space-y-1" role="list">
                            <template v-for="item in visibleManagerNavigation" :key="item.title">
                                <li>
                                    <a
                                        v-if="item.href"
                                        :href="item.href"
                                        :class="linkClass(item, !staticSidebarOpen)"
                                        @click="handleSidebarItemClick(item.name)"
                                    >
                                        <component :is="item.icon" :class="iconClass(item)" aria-hidden="true" />
                                        <span v-if="staticSidebarOpen" class="truncate">{{ item.title }}</span>
                                        <Tooltip v-if="!staticSidebarOpen" :text="item.title" direction="right" />
                                    </a>

                                    <RouterLink
                                        v-else-if="item.name"
                                        :to="{ name: item.name }"
                                        :class="linkClass(item, !staticSidebarOpen)"
                                        :data-tour="sidebarTourAttr(item)"
                                        @click="handleSidebarItemClick(item.name)"
                                    >
                                        <component :is="item.icon" :class="iconClass(item)" aria-hidden="true" />
                                        <span v-if="staticSidebarOpen" class="truncate">{{ item.title }}</span>
                                        <Tooltip v-if="!staticSidebarOpen" :text="item.title" direction="right" />
                                    </RouterLink>
                                </li>
                            </template>
                        </ul>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth-store.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import Tooltip from '@/components/Tooltip.vue';
import { arrangeNavigation, availableNavigation, managerNavigation, navigationEntryId, resolveSidebarSettings } from '@/configs/sidebarNavigation.js';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    openSidebar: { type: Boolean, default: false },
    staticSidebarState: { type: Boolean, required: true },
});
const emit = defineEmits(['update:staticSidebarState', 'sidebarClosed', 'posClicked']);

const auth = useAuthStore();
const router = useRouter();
const configStore = useConfigurationStore();

const sidebarOpen = ref(false);
watch(
    () => props.openSidebar,
    (val) => (sidebarOpen.value = val),
);

const staticSidebarOpen = ref(props.staticSidebarState);
watch(
    () => props.staticSidebarState,
    (val) => (staticSidebarOpen.value = val),
);

const companyLogo = computed(() => configStore.companyLogoWithVersion);
const companyName = computed(() => configStore.company_name);

const currentRouteName = computed(() => router.currentRoute.value.name);
const currentRoutePath = computed(() => router.currentRoute.value.path);

const canShowNavItem = (item) => {
    // Links render through a dynamic component, so an entry with neither target
    // is dropped here rather than handed to the router as { name: undefined }.
    if (!item.href && !item.name) {
        return false;
    }
    if (!auth.hasAccess({ roles: item.roles ?? [], permissions: item.permissions ?? [] })) {
        return false;
    }
    if (item.addon && !configStore.isAddonEnabled(item.addon)) {
        return false;
    }
    // Subscription billing is armed by the vendor setting terms, not by an
    // addon flag, so it has its own test. An installation with no terms is
    // not being charged for anything and has nothing to pay, so the entry is
    // hidden entirely: a link to a page that only says "no terms have been
    // set" is a question the operator did not need to be asked.
    if (item.requiresBillingTerms && !configStore.billingState?.armed) {
        return false;
    }
    return true;
};

// The catalogue arranged as the installation's settings say — grouped or
// flat, renamed, reordered — before this user's permissions and addons are
// applied to it. See resources/js/configs/sidebarNavigation.js.
const arrangedNavigation = computed(() => arrangeNavigation(resolveSidebarSettings(configStore.configurations)));

// A group is visible when at least one of its children is, so a group never
// shows as an empty disclosure and never hides a link the user is allowed to open.
const visibleNavigation = computed(() => availableNavigation(arrangedNavigation.value, canShowNavItem));

// Admin Navigation, through the same predicate as everything else. It used to
// repeat the permission test inline on each row and gate the heading on an OR
// across every admin permission, which meant a row hidden for any other
// reason — an addon, or billing having no terms — still counted towards
// showing the heading. Filtering once fixes both: the heading appears only
// when something is under it.
const visibleManagerNavigation = computed(() => managerNavigation.filter(canShowNavItem));

const SIDEBAR_GROUPS_KEY = 'inventree_sidebar_groups';

const readOpenGroups = () => {
    try {
        const stored = JSON.parse(localStorage.getItem(SIDEBAR_GROUPS_KEY));
        return stored && typeof stored === 'object' ? stored : {};
    } catch (error) {
        return {};
    }
};

const openGroups = ref(readOpenGroups());

watch(
    openGroups,
    (value) => {
        try {
            localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(value));
        } catch (error) {
            // A full or blocked storage quota only costs the remembered state
        }
    },
    { deep: true },
);

const isNavItemActive = (item) => currentRouteName.value === item.name || currentRoutePath.value === item.href;

const groupHasActiveItem = (group) => (group.children ?? []).some(isNavItemActive);

// The group holding the current page is always open, whatever the stored state
// says, so the sidebar can never hide where you are.
const isGroupOpen = (group) => groupHasActiveItem(group) || openGroups.value[group.key] === true;

const toggleGroup = (group) => {
    if (groupHasActiveItem(group)) {
        return;
    }

    openGroups.value = { ...openGroups.value, [group.key]: !openGroups.value[group.key] };
};

const handleSidebarItemClick = (itemName) => {
    if (itemName === 'pos') {
        emit('posClicked');
    }

    sidebarOpen.value = false;
    emit('sidebarClosed');
};

const sidebarTourAttr = (item) => {
    if (item.tourAttr) {
        return item.tourAttr;
    }
    if (!item.name) {
        return undefined;
    }
    const key = String(item.name)
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/[^a-zA-Z0-9_]+/g, '_')
        .toLowerCase();
    return `sidebar-${key}`;
};

const sidebarClosed = () => {
    sidebarOpen.value = false;
    emit('sidebarClosed');
};

// Bind only the prop that applies. Passing `:href="undefined"` to a RouterLink
// does not fall away — it lands in the fallthrough attrs and strips the href
// RouterLink renders, which costs the pointer cursor, middle-click and copy-link.
const linkTarget = (item) => (item.href ? { href: item.href } : { to: { name: item.name } });

const linkClass = (item, collapsed = false) => {
    const base = [
        isNavItemActive(item) ? 'bg-gray-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
        'group flex gap-x-3 rounded-md p-1 text-sm/6 font-semibold',
    ];
    if (collapsed) base.push('justify-center');
    return base.join(' ');
};

// `small` shrinks a group child's icon on the collapsed rail, where there is no
// room to express nesting through indentation.
const iconClass = (item, small = false) => {
    const base = [isNavItemActive(item) ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600', small ? 'h-5 w-5' : 'h-6 w-6', 'shrink-0'];
    return base.join(' ');
};

// Group headers mirror linkClass, minus the active background — the active child
// inside carries that, so both would double up.
const groupClass = (group, collapsed = false) => {
    const base = [
        groupHasActiveItem(group) ? 'text-indigo-600' : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
        'group flex w-full cursor-pointer items-center rounded-md p-1 text-start text-sm/6 font-semibold',
        // The rail has to fit an icon plus its chevron inside 5rem.
        collapsed ? 'justify-center gap-x-0.5' : 'gap-x-3',
    ];
    return base.join(' ');
};

const groupIconClass = (group) => {
    const base = [groupHasActiveItem(group) ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600', 'h-6 w-6 shrink-0'];
    return base.join(' ');
};

onMounted(() => {
    configStore.loadFromCache();
    if (!configStore.loaded) configStore.fetchConfigurations();
});
</script>

<style scoped>
/* The gutter is reserved at all times and only the thumb's colour changes, so
   revealing the bar on hover never reflows the links underneath it.
   `both-edges` mirrors that gutter onto the left, which keeps the icons centred
   in the collapsed rail — the bar itself still renders on the rail's edge. */
.sidebar-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgb(156 163 175 / 0.6) transparent;
    scrollbar-gutter: stable both-edges;
}

.sidebar-scroll::-webkit-scrollbar {
    width: 6px;
}

.sidebar-scroll::-webkit-scrollbar-track {
    background: transparent;
}

.sidebar-scroll::-webkit-scrollbar-thumb {
    border-radius: 9999px;
    background-color: rgb(156 163 175 / 0.6);
}

/* Hover-capable pointers get the hide-until-hovered behaviour. Touch devices
   fall through to the always-visible thumb above, since they never hover. */
@media (hover: hover) {
    .sidebar-scroll {
        scrollbar-color: transparent transparent;
    }

    .sidebar-scroll::-webkit-scrollbar-thumb {
        background-color: transparent;
        transition: background-color 150ms ease-in-out;
    }

    /* Hovering anywhere on the sidebar counts, including the pinned header. */
    .sidebar-panel:hover .sidebar-scroll,
    .sidebar-scroll:focus-within {
        scrollbar-color: rgb(156 163 175 / 0.6) transparent;
    }

    .sidebar-panel:hover .sidebar-scroll::-webkit-scrollbar-thumb,
    .sidebar-scroll:focus-within::-webkit-scrollbar-thumb {
        background-color: rgb(156 163 175 / 0.6);
    }
}
</style>
